import React, { useState, useEffect, useCallback, useRef } from "react";
import { setGlobalSearchValue } from "services/Redux/userToken";
import { History } from "./History";
import { Pending } from "./Pending";
import { Apply } from "./Apply";
import { EditIncentive } from "./EditIncentive";
import DefaultMonthDateRangePicker from "../DateRangePicker";
import * as Mui from "@mui/material";
import { showToast } from "services/utils/Status";
import { useSelector, useDispatch } from "react-redux";
import { themes } from "services/constants";
import sessionHandling from "services/utils/notificationUtils";
import { CustomToggleButton } from "components/CustomComponents/CustomToggleButton";

export const Incentive = () => {
  const dispatch = useDispatch();
  const { domain, token, globalSearchValue, topbarCompanyId, userDetails } =
    useSelector((state) => state.tokenReducer);
  const { departmentId } = useSelector((state) => state.userReducer);
  const hasMounted = useRef(false);
  const [initialRun, setInitialRun] = useState(true);
  const [pendingDetails, setPendingDetails] = useState([]);
  const [listViewPage, setListViewPage] = useState(0);
  const [selectAll, setSelectAll] = useState(false);
  const [searchStartDate, setSearchStartDate] = useState("");
  const [searchEndDate, setSearchEndDate] = useState("");
  const [incentiveDetails, setIncentiveDetails] = useState([]);
  const [loader, setLoader] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState("Pending");
  const [alignment, setAlignment] = useState("Pending");
  const [selectedIncentive, setSelectedIncentive] = useState({});

  const IncentiveGrpButton = [
    {
      value: "Pending",
      label: "Pending",
      onClick: () => {
        setSelectedComponent("Pending");
        dispatch(setGlobalSearchValue(""));

        handleStartDate(
          searchStartDate.split("-").reverse().join("/"),
          searchEndDate.split("-").reverse().join("/"),
          departmentId,
          refreshReCall()
        );
      },
    },
    {
      value: "History",
      label: "History",
      onClick: () => {
        setSelectedComponent("History");
        dispatch(setGlobalSearchValue(""));
      },
    },

    {
      value: "Apply",
      label: "Apply",
      onClick: () => {
        setSelectedComponent("Apply");
        dispatch(setGlobalSearchValue(""));
      },
    },
  ];

  const renderComponent = () => {
    switch (selectedComponent) {
      case "Pending":
        return (
          <Pending
            incentiveDetails={incentiveDetails}
            listViewPage={listViewPage}
            setListViewPage={setListViewPage}
            refreshReCall={refreshReCall}
            setSelectAll={setSelectAll}
            selectAll={selectAll}
            loader={loader}
            setSelectedComponent={setSelectedComponent}
            setSelectedIncentive={setSelectedIncentive}
            pendingDetails={pendingDetails}
          />
        );
      case "History":
        return <History startDate={searchStartDate} endDate={searchEndDate} />;
      case "Apply":
        return <Apply />;
      case "EditIncentive":
        return (
          <EditIncentive
            setSelectedComponent={setSelectedComponent}
            selectedIncentive={selectedIncentive}
          />
        );
      default:
        break;
    }
  };

  const handleStartDate = async (startDate, endDate, id) => {
    if (selectedComponent === "Pending" && searchStartDate && searchEndDate) {
      setSearchStartDate(startDate.split("/").reverse().join("-"));
      setSearchEndDate(endDate.split("/").reverse().join("-"));
      setLoader(true);
      try {
        const response = await fetch(
          `${domain}incentive-applications/?start_date=${searchStartDate}&end_date=${searchEndDate}&company_id=${topbarCompanyId}&user_id=${userDetails?.id}&status=Pending`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          }
        );
        const res = await response.json();
        if (response.ok) {
          setPendingDetails(res);
          setSelectAll(false);
          setLoader(false);
        } else if (response.status === 409) {
          sessionHandling();
        } else {
          throw new Error(res.message);
        }
      } catch (error) {
        showToast("error", error.message);
        setLoader(false);
      }
    } else {
      setSearchStartDate(startDate.split("/").reverse().join("-"));
      setSearchEndDate(endDate.split("/").reverse().join("-"));
    }
  };

  const refreshReCall = useCallback(async () => {
    if (!initialRun && selectedComponent === "Pending") {
      setLoader(true);
      try {
        const response = await fetch(
          `${domain}incentive-applications/?start_date=${searchStartDate}&end_date=${searchEndDate}&company_id=${topbarCompanyId}&user_id=${userDetails?.id}&status=Pending`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          }
        );
        const res = await response.json();
        if (response.ok) {
          setPendingDetails(res);
          setLoader(false);
        } else if (response.status === 409) {
          sessionHandling();
        } else {
          throw new Error(res.error);
        }
      } catch (error) {
        showToast("error", error.message);
        setLoader(false);
      }
    } else {
      setInitialRun(false);
    }
  }, [
    domain,
    initialRun,
    searchEndDate,
    searchStartDate,
    selectedComponent,
    token,
    topbarCompanyId,
    userDetails?.id,
  ]);

  useEffect(() => {
    if (topbarCompanyId) {
      if (!hasMounted.current) {
        hasMounted.current = true;
        return;
      }
      refreshReCall();
    }
  }, [refreshReCall, topbarCompanyId]);

  useEffect(() => {
    const searchFields = [
      "incentive_name",
      "incentive_type",
      "calculation_mode",
      "amount",
      "eligibility_met_employees",
    ];
    const filteredResults = pendingDetails.filter((item) =>
      searchFields.some((key) =>
        item[key]
          ?.toString()
          .toLowerCase()
          .includes(globalSearchValue.toString().toLowerCase())
      )
    );
    setIncentiveDetails(filteredResults);
    setListViewPage(globalSearchValue ? 0 : listViewPage);
  }, [globalSearchValue, listViewPage, pendingDetails]);

  return (
    <React.Fragment>
      <Mui.Grid container sx={{ paddingTop: 3, paddingLeft: 1 }}>
        <Mui.Grid xs={4.4}>
          <CustomToggleButton
            alignment={alignment}
            handleChange={(_, newVal) => newVal && setAlignment(newVal)}
            buttons={IncentiveGrpButton}
            themes={themes}
          />
        </Mui.Grid>
        {selectedComponent === "Apply" ? <Mui.Grid xs={2.65}></Mui.Grid> : null}
        {selectedComponent === "History" || selectedComponent === "Pending" ? (
          <Mui.Grid xs={5}></Mui.Grid>
        ) : null}

        {selectedComponent !== "Apply" &&
        selectedComponent !== "EditIncentive" ? (
          <Mui.Grid xs={2.4} sx={{ paddingLeft: 1 }}>
            <DefaultMonthDateRangePicker
              handleStartDate={handleStartDate}
              showToday={true}
              triggerDate={true}
            />
          </Mui.Grid>
        ) : null}
        <Mui.Grid container>{renderComponent()}</Mui.Grid>
      </Mui.Grid>
    </React.Fragment>
  );
};
